'use client';

import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
// import { useActionState, startTransition } from 'react';

import { AiOutlineHeart, AiOutlineUser, AiFillHeart } from 'react-icons/ai';
import { IoIosArrowDown, IoMdClose } from 'react-icons/io';
import clsx from 'clsx';
import { useAuth, useLoginUrl, useLogOutUrl } from '@skavl-media/subrite-auth/hooks';
import { RxHamburgerMenu } from 'react-icons/rx';
import MenuItems from '../MenuItems';
// import forceCacheUpdate from '@/actions/forceUpdate';
import LinkComponent from '@/components/LinkComponent';
import { useFocusOutside } from '@/hooks/use-focus-outside';
import { useEscapeKey } from '@/hooks/use-escape-key';

type Props = {
  menuItems?: {
    _key: string;
    label: string | null;
    link: string | null;
  }[];
  theme?: 'white' | 'dark' | null;
  pathname?: string;
};

function UserMenu({ menuItems, pathname, theme }: Props) {
  const [isActive, setIsActive] = useState(false);
  const { isLoggedIn, user } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const firstName = user?.nickName || user?.fullName?.split(' ')[0];
  const loginUrl = useLoginUrl();
  const logOutUrl = useLogOutUrl();

  // const [updateCacheState, updateCacheDispatch, isPending] = useActionState(
  //   forceCacheUpdate,
  //   'idle',
  // );

  // const updateCache = () => {
  //   startTransition(() => {
  //     updateCacheDispatch();
  //   });
  // };

  const closeNav = () => {
    setIsActive(false);
  };
  const openNav = () => {
    setIsActive(true);
  };
  const openUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  useFocusOutside(
    userMenuRef,
    () => {
      setUserMenuOpen(false);
    },
    userMenuOpen,
  );

  useEscapeKey(() => {
    setUserMenuOpen(false);
  });

  useEffect(() => {
    if (isActive) {
      document.body.style.removeProperty('overflow');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isActive]);

  return (
    <div className="relative flex items-center justify-end gap-4">
      <button
        className={clsx('block lg:hidden', {
          'text-white': theme === 'white',
          'text-black': theme === 'dark',
        })}
        onClick={openNav}
        aria-expanded={isActive}
        aria-label="åpne navigasjons meny"
      >
        <RxHamburgerMenu size={30} />
      </button>
      <div
        className={clsx(
          'fixed right-0 top-0 z-10 h-full w-full lg:static lg:block lg:translate-y-0 lg:bg-transparent lg:transition-none',
          {
            '100ms flex translate-y-0 flex-col items-center justify-start transition-transform':
              isActive,
            'hidden -translate-y-full': !isActive,
          },
          {
            'bg-white': theme === 'white',
            'bg-black': theme === 'dark',
          },
        )}
        ref={userMenuRef}
      >
        <button
          className={clsx('self-end px-4 py-7 lg:hidden', {
            'text-white': theme === 'dark',
            'text-black': theme === 'white',
          })}
          aria-label="Lukk navigasjons meny"
          onClick={closeNav}
        >
          <IoMdClose size={34} />
        </button>
        <div className="block lg:hidden">
          <MenuItems
            menuItems={menuItems}
            pathname={pathname}
            closeNav={closeNav}
            theme={theme === 'white' ? 'dark' : 'white'}
          />
        </div>
        {/** the diviver in the mobile menu */}
        <hr
          className={clsx('my-10 w-1/2 border-t text-center lg:hidden', {
            'border-white': theme === 'dark',
            'border-black': theme === 'white',
          })}
        />
        {!isLoggedIn ? (
          <ul className="flex flex-col items-center gap-5 lg:flex-row">
            <li onClick={closeNav}>
              <LinkComponent
                variants="primary"
                className={clsx(
                  'inline-flex h-11 w-full max-w-44 items-center justify-center whitespace-nowrap rounded-md px-8 font-bold uppercase lg:h-[50px] lg:w-auto lg:text-base',
                  {
                    'bg-white text-black': theme === 'white',
                    'bg-black text-white': theme === 'dark',
                  },
                )}
                href={`/abonnement?reference=${encodeURIComponent('/#menu')}`}
              >
                Bli medlem
              </LinkComponent>
            </li>
            <li onClick={closeNav}>
              <LinkComponent
                variants="primary-outline"
                className={clsx(
                  'inline-flex h-11 w-full max-w-44 items-center justify-center whitespace-nowrap rounded-md px-8 font-bold uppercase ring-1 lg:h-[50px] lg:w-auto lg:text-base',
                  {
                    'ring-white text-white':
                      (theme === 'white' && !isActive) || (theme === 'dark' && isActive),
                    'ring-black text-black':
                      (theme === 'dark' && !isActive) || (theme === 'white' && isActive),
                  },
                )}
                href={loginUrl}
              >
                Logg inn
              </LinkComponent>
            </li>
          </ul>
        ) : (
          <>
            <button
              onClick={openUserMenu}
              className={clsx(
                'text-md hidden items-center justify-center gap-2 rounded-3xl px-4 py-2 hover:ring-1 active:ring-1 lg:flex',
                {
                  'hover:ring-white': theme === 'white',
                  'hover:ring-black': theme === 'dark',
                },
              )}
            >
              {user?.avatar ? (
                <Image
                  src={user?.avatar ?? ''}
                  alt={firstName ?? 'Bruker avatar'}
                  className="h-8 w-8 rounded-full"
                  height={30}
                  width={30}
                />
              ) : (
                <span
                  className={clsx('text-lg', {
                    'text-white': theme === 'white',
                    'text-black': theme === 'dark',
                  })}
                >
                  <AiOutlineUser size={24} />
                </span>
              )}
              <span
                className={clsx('text-lg', {
                  'text-white': theme === 'white',
                  'text-black': theme === 'dark',
                })}
              >
                {firstName}
              </span>

              <IoIosArrowDown
                className={clsx(
                  'transform transition-transform',
                  {
                    'rotate-180': userMenuOpen,
                  },
                  {
                    'text-white': theme === 'white',
                    'text-black': theme === 'dark',
                  },
                )}
              />
            </button>
            <ul className="flex flex-col items-center gap-5 text-xl lg:hidden lg:flex-row">
              <li onClick={closeNav}>
                <LinkComponent
                  variants="primary"
                  className={clsx(
                    'inline-flex h-11 w-full max-w-44 items-center justify-center gap-x-2 whitespace-nowrap rounded-full px-8 font-semibold uppercase lg:h-[50px] lg:w-auto lg:text-base',
                    {
                      'bg-white text-black': theme === 'white',
                      'bg-black text-white': theme === 'dark',
                    },
                  )}
                  href={process.env.NEXT_PUBLIC_SUBRITE_URL as string}
                >
                  <AiFillHeart />
                  <span>Minside</span>
                </LinkComponent>
              </li>
              <li className="mr-3 font-medium" onClick={closeNav}>
                <LinkComponent
                  href={logOutUrl}
                  variants="primary"
                  className={clsx(
                    'inline-flex h-11 w-full max-w-44 items-center justify-center gap-x-2 whitespace-nowrap rounded-full px-8 font-semibold uppercase lg:h-[50px] lg:w-auto lg:text-base',
                    {
                      'bg-white text-black': theme === 'white',
                      'bg-black text-white': theme === 'dark',
                    },
                  )}
                >
                  Logg ut
                </LinkComponent>
              </li>

              {/* {isAdmin && (
                <>
                  <hr className="border-light-teal my-10 w-1/2 border-t text-center lg:hidden" />
                  <li>
                    <button
                      onClick={updateCache}
                      className="hover:text-primary-bg flex w-full items-center gap-2 px-6 py-4 text-sm"
                    >
                      Force Update {isPending && '...'}
                      {updateCacheState !== 'idle'
                        ? updateCacheState === 'success'
                          ? '✅'
                          : '❌'
                        : ''}
                    </button>
                  </li>
                </>
              )} */}
            </ul>
          </>
        )}
        {userMenuOpen && (
          <ul
            className={clsx(
              'right-[0.5rem] top-[3rem] hidden gap-2 rounded-md p-4 text-lg shadow-lg lg:absolute lg:flex lg:flex-col divide-y',
              {
                'bg-white divide-gray-300': theme === 'white',
                'bg-black divide-white': theme === 'dark',
              },
            )}
          >
            <li>
              <a
                tabIndex={0}
                className={clsx('flex w-full items-center gap-2 px-6 py-1', {
                  'bg-white text-black': theme === 'white',
                  'bg-black text-white': theme === 'dark',
                })}
                href={process.env.NEXT_PUBLIC_SUBRITE_URL as string}
                title="min side"
                onClick={closeNav}
              >
                <AiOutlineHeart />
                <span>Minside</span>
              </a>
            </li>

            <li onClick={closeNav}>
              <LinkComponent
                href={logOutUrl}
                variants="primary"
                className={clsx('flex w-full items-center gap-2 px-6 py-1 justify-center', {
                  'bg-white text-black': theme === 'white',
                  'bg-black text-white': theme === 'dark',
                })}
              >
                Logg ut
              </LinkComponent>
            </li>
            {/* {isAdmin && (
              <>
                <hr className="border-light-teal my-10 w-1/2 border-t text-center lg:hidden" />
                <li>
                  <button
                    onClick={updateCache}
                    className="hover:text-primary-bg flex w-full items-center gap-2 px-6 py-4 text-sm"
                  >
                    Force Update
                  </button>
                </li>
              </>
            )} */}
          </ul>
        )}
      </div>
    </div>
  );
}

export default UserMenu;
